/**
 * Zod schemas for the highest-traffic API response shapes.
 *
 * Wired into `src/services/api.ts` so we fail loudly with a parse error
 * (toasted by the global QueryCache `onError`) when the backend drifts —
 * instead of letting an unchecked cast surface as a downstream
 * NaN/undefined render bug.
 *
 * Conventions:
 *   • Root objects use `.passthrough()` so backend-only fields the
 *     frontend doesn't read aren't stripped (they ride along as
 *     `unknown` properties on the parsed value).
 *   • Optional fields use `.optional()`; nullable fields use
 *     `.nullable()`; both when both are reachable on the wire.
 *   • Dates stay as `z.string()` (ISO) — callers `new Date(...)` on
 *     them themselves; we never transform.
 *   • Types are inferred via `z.infer<typeof ...>` and re-exported from
 *     the original `src/models/*.ts` paths so existing imports keep
 *     resolving.
 *
 * Only schemas listed here are wired in api.ts. Endpoints not on the
 * priority list keep their plain `axios.get<T>` cast for now.
 */

import { z } from "zod";

// ── AuthUser (`/auth/me`, `PATCH /users/me`) ───────────────────────────────

export const AuthUserSchema = z
  .object({
    user_id: z.string(),
    email: z.string(),
    name: z.string(),
    name_override: z.string().nullable().optional(),
    name_google: z.string().nullable().optional(),
    picture: z.string().optional(),
    created_at: z.string().nullable().optional(),
    last_login_at: z.string().nullable().optional(),
    is_admin: z.boolean().optional(),
  })
  .passthrough();

export type AuthUser = z.infer<typeof AuthUserSchema>;

// ── ChatMessage + MessagesPage (`/sessions/:id/messages`) ─────────────────

export const ToolArtifactSchema = z
  .object({
    // `kind` is "image" | "chart" | "link" | string — keep it loose to
    // match the type's open-ended discriminator.
    kind: z.string(),
    url: z.string(),
    label: z.string().optional(),
    content_type: z.string().optional(),
  })
  .passthrough();

export type ToolArtifact = z.infer<typeof ToolArtifactSchema>;

export const ToolCallSchema = z
  .object({
    name: z.string(),
    args: z.record(z.unknown()),
    result: z.unknown(),
    summary: z.string().optional(),
    artifacts: z.array(ToolArtifactSchema).nullable().optional(),
    ts: z.string().nullable().optional(),
  })
  .passthrough();

export type ToolCall = z.infer<typeof ToolCallSchema>;

export const ChatMessageSchema = z
  .object({
    id: z.string().optional(),
    role: z.enum(["user", "assistant"]),
    content: z.string(),
    mermaid: z.array(z.string()).nullable().optional(),
    is_bom: z.boolean(),
    timestamp: z.string().optional(),
    author_user_id: z.string().nullable().optional(),
    author_name: z.string().nullable().optional(),
    author_picture: z.string().nullable().optional(),
    tool_calls: z.array(ToolCallSchema).nullable().optional(),
  })
  .passthrough();

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const MessagesPageSchema = z
  .object({
    messages: z.array(ChatMessageSchema),
    has_more: z.boolean(),
    oldest_timestamp: z.number().nullable(),
  })
  .passthrough();

export type MessagesPage = z.infer<typeof MessagesPageSchema>;

// ── ChatSessionSummary (`/sessions`, `POST /sessions`, etc) ────────────────

export const ChatSessionSummarySchema = z
  .object({
    session_id: z.string(),
    title: z.string(),
    project_id: z.string().nullable(),
    active_project_name: z.string().nullable().optional(),
    active_version_id: z.string().nullable().optional(),
    active_version_number: z.number().nullable().optional(),
    /** Pinned chat surface. Null on legacy sessions created before the
     *  field landed; treated as workbench at runtime. */
    surface: z.enum(["workbench", "requirements"]).nullable().optional(),
    /** Multi-select board scope. Empty list = whole project; one or
     *  more entries = pinned subset. Mirrors the chat composer's
     *  scope chip; immutable once set. */
    board_ids: z.array(z.string()).optional().default([]),
    // `tool_ids` + `loadout_changes` were retired in May 2026 with
    // the Workbench Tool picker. The schema's `.passthrough()` lets
    // any legacy persisted fields pass without validation; new code
    // shouldn't read them.
    created_at: z.string(),
    last_active: z.string(),
    message_count: z.number(),
    last_message_preview: z.string().optional(),
    organisation_id: z.string().nullable().optional(),
    creator_user_id: z.string().nullable().optional(),
    creator_name: z.string().nullable().optional(),
    creator_picture: z.string().nullable().optional(),
  })
  .passthrough();

export type ChatSessionSummary = z.infer<typeof ChatSessionSummarySchema>;

// ── Project (`GET /projects`) ──────────────────────────────────────────────

const ConnectorKindSchema = z.enum([
  "power_in",
  "power_out",
  "data_bus",
  "sensor",
  "debug",
  "display",
  "actuator",
  "other",
]);

const ConnectorAnnotationSchema = z
  .object({
    component_uuid: z.string(),
    kind: ConnectorKindSchema,
    note: z.string(),
  })
  .passthrough();

const BoardEnrichmentSummarySchema = z
  .object({
    total_ics: z.number().optional(),
    ok: z.number().optional(),
    likely_wrong_mpn: z.number().optional(),
    failed: z.number().optional(),
    pending: z.number().optional(),
    status: z.enum(["none", "in_progress", "partial", "done"]).optional(),
  })
  .passthrough();

export const BoardSchema = z
  .object({
    id: z.string(),
    project_id: z.string(),
    name: z.string(),
    description_md: z.string().nullable().optional(),
    current_version_id: z.string().nullable().optional(),
    current_version_number: z.number().nullable().optional(),
    current_version_semver: z.string().nullable().optional(),
    current_version_uploaded_at: z.string().nullable().optional(),
    component_count: z.number().optional(),
    net_count: z.number().optional(),
    updated_at: z.string().nullable().optional(),
    kind: z.enum(["main", "candidate", "fixture", "scratch"]).optional(),
    status: z
      .enum(["ephemeral", "draft", "active", "released", "archived"])
      .optional(),
    connector_annotations: z.array(ConnectorAnnotationSchema).optional(),
    enrichment: BoardEnrichmentSummarySchema.nullable().optional(),
  })
  .passthrough();

export const ProjectSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    user_id: z.string(),
    organisation_id: z.string().nullable().optional(),
    brief_md: z.string().nullable().optional(),
    status: z.enum(["active", "archived"]).optional(),
    archived_at: z.string().nullable().optional(),
    boards: z.array(BoardSchema).optional(),
    cable_count: z.number().optional(),
    component_count: z.number().optional(),
    net_count: z.number().optional(),
    updated_at: z.string().nullable().optional(),
    created_at: z.string().optional(),
    // Workbench v2 canary flag. When true, the project's Workbench
    // tab swaps to the new TestsTab UI. Defaults to false everywhere;
    // server reads from `app.project.workbench_v2_enabled`.
    workbench_v2_enabled: z.boolean().optional(),
  })
  .passthrough();

export type Project = z.infer<typeof ProjectSchema>;

// ── UploadResponse (`POST /projects/upload`, `/upload-design`) ─────────────

// `ChangeSummary` is rich and nested; the upload response permits an
// empty `{}` placeholder OR the full summary. We model the full shape
// with optional fields so an empty `{}` parses (every field is
// optional via `.partial()`-by-construction) and a real summary
// validates its sub-fields.
const ChangeSummaryFieldChangeSchema = z
  .object({ from: z.string(), to: z.string() })
  .passthrough();

const ChangeSummaryComponentModifiedSchema = z
  .object({
    ref: z.string(),
    changes: z.record(ChangeSummaryFieldChangeSchema),
  })
  .passthrough();

const ChangeSummaryNetTopologyChangeSchema = z
  .object({
    name: z.string(),
    pins_added: z.array(z.string()),
    pins_removed: z.array(z.string()),
  })
  .passthrough();

const ChangeSummaryTotalsSchema = z
  .object({
    components_added: z.number(),
    components_removed: z.number(),
    components_modified: z.number(),
    nets_added: z.number(),
    nets_removed: z.number(),
    nets_topology_changed: z.number(),
  })
  .passthrough();

// Permits both the rich `ChangeSummary` and the empty-object placeholder
// the upload endpoint returns when the version is brand-new.
const ChangeSummaryOrEmptySchema = z
  .object({
    components: z
      .object({
        added: z.array(z.string()).optional(),
        removed: z.array(z.string()).optional(),
        modified: z.array(ChangeSummaryComponentModifiedSchema).optional(),
      })
      .passthrough()
      .optional(),
    nets: z
      .object({
        added: z.array(z.string()).optional(),
        removed: z.array(z.string()).optional(),
        topology_changed: z
          .array(ChangeSummaryNetTopologyChangeSchema)
          .optional(),
      })
      .passthrough()
      .optional(),
    totals: ChangeSummaryTotalsSchema.optional(),
  })
  .passthrough();

export const UploadResponseSchema = z
  .object({
    status: z.enum(["created", "deduped"]),
    message: z.string(),
    // `project` (the project name) was the legacy identifier the backend
    // returned pre-UUID-native refactor. Backend now returns `project_id`
    // (wrapper UUID) + `board_id` + `board_name` instead — the project
    // *name* isn't on the response any more. Both names are kept optional
    // here so cached / replayed responses from the old shape don't blow
    // up on parse, but no consumer should reach for `project` going forward.
    project: z.string().optional(),
    project_id: z.string().nullable().optional(),
    board_id: z.string().nullable().optional(),
    board_name: z.string().nullable().optional(),
    version_id: z.string().nullable().optional(),
    version_number: z.number().nullable().optional(),
    semver_label: z.string().nullable().optional(),
    content_hash: z.string().nullable().optional(),
    source_file_uri: z.string().nullable().optional(),
    component_count: z.number(),
    net_count: z.number(),
    diff: ChangeSummaryOrEmptySchema,
    prefetch_started: z.boolean().optional(),
    detected_format: z.string().nullable().optional(),
    lossy_warnings: z.array(z.string()).optional(),
  })
  .passthrough();

export type UploadResponse = z.infer<typeof UploadResponseSchema>;

// ── ExtractionStatus (`GET /projects/:id/extraction-status`) ───────────────

export const ExtractionStatusSchema = z
  .object({
    status: z.string(),
    progress: z.number().nullable().optional(),
    parts: z.record(z.unknown()).optional(),
  })
  .passthrough();

export type ExtractionStatus = z.infer<typeof ExtractionStatusSchema>;

// ── BoardGraph (`GET /projects/:id/graph`) ─────────────────────────────────

const GraphComponentSchema = z
  .object({
    id: z.string(),
    ref: z.string(),
    value: z.string(),
    footprint: z.string(),
    mpn: z.string(),
    lcsc: z.string(),
    description: z.string(),
    datasheet: z.string(),
    pin_count: z.number(),
    mpn_from_netlist: z.string().optional(),
    mpn_from_bom: z.string().optional(),
    lcsc_from_netlist: z.string().optional(),
    lcsc_from_bom: z.string().optional(),
    value_from_netlist: z.string().optional(),
    value_from_bom: z.string().optional(),
    ingestion_fault: z.boolean().nullable().optional(),
    created_by: z.string().nullable().optional(),
    ai_label: z.string().nullable().optional(),
    role: z
      .enum(["DECOUPLING", "PULLUP", "PULLDOWN", "XTAL_LOAD"])
      .nullable()
      .optional(),
    role_owner_refdes: z.string().nullable().optional(),
    role_confidence: z.enum(["high", "medium", "low"]).nullable().optional(),
  })
  .passthrough();

const GraphNetSchema = z
  .object({
    name: z.string(),
    pin_count: z.number(),
  })
  .passthrough();

const GraphEdgeSchema = z
  .object({
    component_ref: z.string(),
    net_name: z.string(),
    pin: z.string(),
  })
  .passthrough();

export const BoardGraphSchema = z
  .object({
    board_id: z.string().nullable(),
    board_name: z.string().nullable(),
    version_id: z.string().nullable().optional(),
    components: z.array(GraphComponentSchema),
    nets: z.array(GraphNetSchema),
    edges: z.array(GraphEdgeSchema),
  })
  .passthrough();

export type BoardGraph = z.infer<typeof BoardGraphSchema>;

// ── UsageSummary (`GET /usage/summary`) — high-traffic billing-adjacent ────

export const UsageSummarySchema = z
  .object({
    scope: z.string(),
    total_usd: z.number(),
    trace_count: z.number(),
    range_start: z.string(),
    range_end: z.string(),
    source: z.literal("langfuse"),
  })
  .passthrough();

export type UsageSummary = z.infer<typeof UsageSummarySchema>;

// ── OrganisationMember (`GET /orgs/:id/members`) ───────────────────────────

const OrgRoleSchema = z.enum(["owner", "admin", "editor", "viewer"]);

export const OrganisationMemberSchema = z
  .object({
    user_id: z.string(),
    role: OrgRoleSchema,
    joined_at: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    picture: z.string().nullable(),
  })
  .passthrough();

export type OrganisationMember = z.infer<typeof OrganisationMemberSchema>;
